// pd.js - Patio Door Template Component
// This is a reusable template for patio door configurations
// Similar to ww.js for windows, designed for extensibility

import React, { useState, useEffect, useRef } from 'react';
import { Tooltip, IconButton, Box } from '@mui/material';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { UrlDev } from '../../url';
import { authFetch } from '../../utils/api';
import { alertC, confirmC } from '../../components/dialogService';

import { useCrmSettings } from '../../CRM/SimpleCRM/CrmSettingsContext';
import { SCALE } from '../../constants';
import { CANVASW, CANVASH, INITX, INITY } from '../../constants';
import DrawWindow from '../../DrawWindow';

import ExpandableSection from '../../expandablesection';
import '../../entrydoor.css';
import '../../toolbar.css';
import '../../spinner.css';
import '../../flip.css';

import { fetchUserField } from '../../utils/userSettings';

// Patio Door Controls
import PatioDoorType from '../controls/configuration/PatioDoorType';
import UnitWidth from '../controls/frame/UnitWidth';
import UnitHeight from '../controls/frame/UnitHeight';
import OperationVenting from '../controls/frame/OperationVenting';
import GrillSizeType from '../controls/grill/GrillSizeType';
import GrillPatternSimple from '../controls/grill/GrillPatternSimple';
import HorizontalGrills from '../../windows/controls/grill/HorizontalGrills';
import VerticalGrills from '../../windows/controls/grill/VerticalGrills';
import PrairieDividers from '../controls/grill/PrairieDividers';
import CottageOptions from '../controls/grill/CottageOptions';
import SpacerColor from '../controls/grill/SpacerColor';
import GrillColor from '../../windows/controls/grill/GrillColor';
import GlazingType from '../../windows/controls/glass/GlazingType';
import GlassEnergy from '../../windows/controls/glass/GlassEnergy';
import GlassStrength from '../../windows/controls/glass/GlassStrength';
import YesNo from '../controls/general/YesNo';
import LockFinish from '../controls/glass/LockFinish';
import LockSeries from '../controls/glass/LockSeries';
import WinColorFull from '../../windows/controls/finish/WinColorFull';
import ColorFinishing from '../../windows/controls/finish/ColorFinishing';

// Installation Controls (shared from windows)
import InstallationType from '../../windows/controls/installation/InstallationType';
import ExteriorType from '../../windows/controls/installation/ExteriorType';
import BrickmoldSize from '../../windows/controls/installation/BrickmoldSize';
import BrickmoldColor from '../../windows/controls/installation/BrickmoldColor';
import JambType from '../../windows/controls/installation/JambType';
import InteriorCasing from '../../windows/controls/installation/InteriorCasing';
import InteriorCasingSize from '../../windows/controls/installation/InteriorCasingSize';
import SillNose from '../controls/installation/SillNose';

import LocationTag from '../../controls/LocationTag';
import Notes from '../../controls/Notes';
import Discount from '../../controls/frame/Discount';
import Quantity from '../../controls/frame/Quantity';
import CascadingCombo from '../../additional/CascadingCombo';
import { toPdf, toPdfAuto } from '../../components/toPdf';
import EmailDialog from '../../records/EmailDialog';
import { sendEmailWithPdf } from '../../components/sendEmailWithPdf';

import ViewDetails from '../../docs/viewdetails';
import { convertToImperial, formatToDecimal } from '../../controls/conversions';
import { formatToFractional } from '../../controls/conversions';
import { getCookie } from '../../utils/cookies';
import { get_crm_user_id } from '../../CRM/SimpleCRM/crmUtils';
import { authFetchRequest } from '../../utils/api';
import { getCustomerEmail } from '../../utils/QuickDataAccess';
import { createCrmProduct, updateCrmProduct } from '../../CRM/ZohoCrm';
// DrawPatioDoor component would be created separately
// import DrawPatioDoor from '../../DrawPatioDoor';

// Floating menus - shared from windows
import SmallFloatingNavMenu from '../../windows/wint/components/SmallFloatingNavMenu';
import LargeFloatingNavMenu from '../../windows/wint/components/LargeFloatingNavMenu';
import TopBarNavMenu from '../../windows/wint/components/TopBarNavMenu';
import TopBarToolbarMenu from '../../windows/wint/components/TopBarToolbarMenu';
import { useThemeColors } from '../../utils/useThemeColors';
import SmallFloatingToolbarMenu from '../../windows/wint/components/SmallFloatingToolbarMenu';

// Template constants - these should be overridden by config
const DEFAULT_QUOTE_TEMPLATE = "pd_template";
const DEFAULT_PRICE_RULES = null;
const DEFAULT_DOCUMENT_TYPE = "patiodoors";
const DEFAULT_ITEM_PATH = "patiodoors/pdt/pd1";

/**
 * PD - Patio Door Template Component
 *
 * This is a template component designed for patio door configurations.
 * It provides the general framework (navigation, save/load, PDF generation, etc.)
 * but relies on external control map sections for the actual controls.
 *
 * Props:
 * - config: Configuration object with default values and settings
 * - controlMapSections: Array of functions that return control map items
 * - DrawComponent: Component to render the patio door drawing (optional)
 * - documentType: Type of document (default: "patiodoors")
 * - itemPath: Path for the item type (default: "patiodoors/pdt/pd1")
 * - quoteTemplate: Template name for quotes
 * - priceRules: Price rules to use
 */
const PD = ({
  config = {},
  controlMapSections = [],
  DrawComponent = DrawWindow,
  documentType = DEFAULT_DOCUMENT_TYPE,
  itemPath = DEFAULT_ITEM_PATH,
  quoteTemplate = DEFAULT_QUOTE_TEMPLATE,
  priceRules = DEFAULT_PRICE_RULES,
}) => {
  const { settings: crmSettings } = useCrmSettings();
  const crmDefined = crmSettings.external_crm !== 'none';
  const tc = useThemeColors();

  // ============================================
  // UI STATE
  // ============================================
  const [showOriginalToolbar, setShowOriginalToolbar] = useState(false);
  const [showOnlySelectedSection, setShowOnlySelectedSection] = useState(false);
  const [enableFloatingNavMenu, setEnableFloatingNavMenu] = useState(true);
  const [enableFloatingToolbarMenu, setEnableFloatingToolbarMenu] = useState(true);
  const [useSmallNavMenu, setUseSmallNavMenu] = useState(true);
  // Docked top-bar nav+toolbar vs floating menus, driven by the user's
  // navMenuStyle preference (Home → Preferences). Top bar is the default.
  const [showTopBar, setShowTopBar] = useState(true);
  const [selectedSectionId, setSelectedSectionId] = useState('Configuration');
  const [expandedSections, setExpandedSections] = useState({ 'Configuration': true });

  // ============================================
  // NAVIGATION & ROUTING
  // ============================================
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();

  // ============================================
  // QUOTE/ITEM STATE
  // ============================================
  const [quoteId, setQuoteId] = useState();
  const [quoteItemId, setQuoteItemId] = useState();
  const [fkCustomerId, setFkCustomerId] = useState();
  const [fkUserId, setFkUserId] = useState();
  const [viewItem, setViewItem] = useState();
  const [reference_no, setReferenceNo] = useState();
  const [newQuoteItemId, setNewQuoteItemId] = useState(null);
  const [lastDateSaved, setLastDateSaved] = useState("NA");
  const [isDisabled, setIsDisabled] = useState(false);

  // ============================================
  // DATA STATE
  // ============================================
  const [doorData, setDoorData] = useState({});
  const [initialDoorDataJSON, setInitialDoorDataJSON] = useState(null);
  const [isViewDetailsVisible, setIsViewDetailsVisible] = useState(false);
  const [viewDetailsData, setViewDetailsData] = useState([]);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [displayPrices, setDisplayPrices] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // ============================================
  // PATIO DOOR SPECIFIC STATE
  // These are the basic states - control maps will add more via sectionProps
  // ============================================
  const doorTypeLabels = { Sliding: "Sliding Door", TripleSliding: "Triple Sliding Door", QuadSliding: "Quad Sliding Door" };
  const [patioDoorTypeValue, setPatioDoorTypeValue] = useState({ value: config.DOOR_TYPE || "Sliding", label: doorTypeLabels[config.DOOR_TYPE] || "Sliding Door" });
  const [unitWidthValue, setUnitWidthValue] = useState({ value: config.UNIT_WIDTH || '58 3/4"', label: config.UNIT_WIDTH || '58 3/4"' });
  const [unitHeightValue, setUnitHeightValue] = useState({ value: config.UNIT_HEIGHT || '79 1/4"', label: config.UNIT_HEIGHT || '79 1/4"' });
  const [operationVentingValue, setOperationVentingValue] = useState({ value: config.OPERATION_VENTING || 'XO(OSLI)', label: config.OPERATION_VENTING || 'XO(OSLI)' });
  const [panelConfigValue, setPanelConfigValue] = useState(config.PANEL_CONFIG || "2");

  // Glass
  const [glassTypeValue, setGlassTypeValue] = useState({ value: "", label: "" });
  const [glazingTypeValue, setGlazingTypeValue] = useState("Double");
  const [glassEnergyValue, setGlassEnergyValue] = useState({ value: "LowE 272", label: "LowE 272" });
  const [glassStrengthValue, setGlassStrengthValue] = useState({ value: "", label: "" });
  const [petMeshValue, setPetMeshValue] = useState({ value: "No", label: "No" });
  const [keyLockValue, setKeyLockValue] = useState({ value: "No", label: "No" });
  const [kickLockValue, setKickLockValue] = useState({ value: "No", label: "No" });
  const [topBoltValue, setTopBoltValue] = useState({ value: "No", label: "No" });
  const [lockFinishValue, setLockFinishValue] = useState({ value: "White", label: "White" });
  const [lockSeriesValue, setLockSeriesValue] = useState({ value: "", label: "" });

  // Finish
  const [exteriorColorValue, setExteriorColorValue] = useState({ value: "White", label: "White" });
  const [interiorColorValue, setInteriorColorValue] = useState({ value: "White", label: "White" });
  const [colorFinishingInteriorValue, setColorFinishingInteriorValue] = useState("None");
  const [colorFinishingExteriorValue, setColorFinishingExteriorValue] = useState("None");

  // Grills - Group 1
  const [grillSizeType1Value, setGrillSizeType1Value] = useState({ value: "", label: "" });
  const [grillPatternSimple1Value, setGrillPatternSimple1Value] = useState({ value: "", label: "" });
  const [horizontalGrills1Value, setHorizontalGrills1Value] = useState("");
  const [verticalGrills1Value, setVerticalGrills1Value] = useState("");
  const [prairieDividers1Value, setPrairieDividers1Value] = useState({ value: "standard", label: "Standard" });
  const [cottageOptions1Value, setCottageOptions1Value] = useState({ value: "1/4", label: "1/4" });
  const [spacerColor1Value, setSpacerColor1Value] = useState({ value: "", label: "" });
  // Grills - Group 2
  const [grillSizeType2Value, setGrillSizeType2Value] = useState({ value: "", label: "" });
  const [grillPatternSimple2Value, setGrillPatternSimple2Value] = useState({ value: "", label: "" });
  const [horizontalGrills2Value, setHorizontalGrills2Value] = useState("");
  const [verticalGrills2Value, setVerticalGrills2Value] = useState("");
  const [prairieDividers2Value, setPrairieDividers2Value] = useState({ value: "standard", label: "Standard" });
  const [cottageOptions2Value, setCottageOptions2Value] = useState({ value: "1/4", label: "1/4" });
  const [spacerColor2Value, setSpacerColor2Value] = useState({ value: "", label: "" });
  // Grills - Group 3
  const [grillSizeType3Value, setGrillSizeType3Value] = useState({ value: "", label: "" });
  const [grillPatternSimple3Value, setGrillPatternSimple3Value] = useState({ value: "", label: "" });
  const [horizontalGrills3Value, setHorizontalGrills3Value] = useState("");
  const [verticalGrills3Value, setVerticalGrills3Value] = useState("");
  const [prairieDividers3Value, setPrairieDividers3Value] = useState({ value: "standard", label: "Standard" });
  const [cottageOptions3Value, setCottageOptions3Value] = useState({ value: "1/4", label: "1/4" });
  const [spacerColor3Value, setSpacerColor3Value] = useState({ value: "", label: "" });
  // Grills - Group 4
  const [grillSizeType4Value, setGrillSizeType4Value] = useState({ value: "", label: "" });
  const [grillPatternSimple4Value, setGrillPatternSimple4Value] = useState({ value: "", label: "" });
  const [horizontalGrills4Value, setHorizontalGrills4Value] = useState("");
  const [verticalGrills4Value, setVerticalGrills4Value] = useState("");
  const [prairieDividers4Value, setPrairieDividers4Value] = useState({ value: "standard", label: "Standard" });
  const [cottageOptions4Value, setCottageOptions4Value] = useState({ value: "1/4", label: "1/4" });
  const [spacerColor4Value, setSpacerColor4Value] = useState({ value: "", label: "" });
  // Grill Colors
  const [grillColorInteriorValue, setGrillColorInteriorValue] = useState({ value: "", label: "" });
  const [grillColorExteriorValue, setGrillColorExteriorValue] = useState({ value: "", label: "" });

  // Installation
  const [installationTypeValue, setInstallationTypeValue] = useState({ value: "None", label: "None" });
  const [exteriorTypeValue, setExteriorTypeValue] = useState({ value: "None", label: "None" });
  const [brickmoldSizeValue, setBrickmoldSizeValue] = useState({ value: "None", label: "None" });
  const [brickmoldColorValue, setBrickmoldColorValue] = useState({ value: "#FFFFFF", label: "White" });
  const [jambTypeValue, setJambTypeValue] = useState({ value: "None", label: "None" });
  const [interiorCasingValue, setInteriorCasingValue] = useState({ value: "None", label: "None" });
  const [interiorCasingSizeValue, setInteriorCasingSizeValue] = useState({ value: "", label: "" });
  const [factoryAssemblyValue, setFactoryAssemblyValue] = useState({ value: "No", label: "No" });
  const [sillNoseValue, setSillNoseValue] = useState({ value: "None", label: "None" });

  // Additional Items
  const requiredKeys = ["CascadingCombo1", "CascadingCombo2", "CascadingCombo3", "CascadingCombo4", "CascadingCombo5"];
  const [CascadingComboValue,  setCascadingComboValue]   = useState([]);
  const [CascadingComboValue1, setCascadingComboValue1]  = useState();
  const [CascadingComboValue2, setCascadingComboValue2]  = useState();
  const [CascadingComboValue3, setCascadingComboValue3]  = useState();
  const [CascadingComboValue4, setCascadingComboValue4]  = useState();
  const [CascadingComboValue5, setCascadingComboValue5]  = useState();

  // Notes & Discounts
  const [locationTagValue, setLocationTagValue] = useState("");
  const [notesValue, setNotesValue] = useState("");
  const [quantityValue, setQuantityValue] = useState("1");
  const [discountValue, setDiscountValue] = useState(0);

  // ============================================
  // CRM STATE
  // ============================================
  const [crm_product_id, set_crm_product_id] = useState();
  const [crmUserId, setCrmUserId] = useState(false);
  const [ctrl_discount_enabled, set_ctrl_discount_enabled] = useState(0);

  // ============================================
  // EMAIL DIALOG STATE
  // ============================================
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [customerEmail, setCustomerEmail] = useState("");
  const [showAltToolbar, setShowAltToolbar] = useState(false);
  const [zoom, setZoom] = useState(1.00);
  const [flipped, setFlipped] = useState(false);

  // ============================================
  // INITIALIZATION
  // ============================================
  useEffect(() => {
    const init = async () => {
      const id = await get_crm_user_id();
      setCrmUserId(id);

      const ctrl_discount_status = await fetchUserField('ctrl_discount_enabled');
      set_ctrl_discount_enabled(ctrl_discount_status);

      const navMenuPref = await fetchUserField('enableFloatingNavMenu');
      setEnableFloatingNavMenu(navMenuPref ?? true);
      const toolbarMenuPref = await fetchUserField('enableFloatingToolbarMenu');
      setEnableFloatingToolbarMenu(toolbarMenuPref ?? true);
      const navStylePref = await fetchUserField('navMenuStyle');
      setShowTopBar(navStylePref !== 'floating');

      const crmProductId = searchParams.get("id") || "NA";
      let inputData = await authFetch(`/local/get-ww-input-data/${crmProductId}`);

      if (inputData?.quoteItemId) {
        setQuoteId(inputData.quoteId);
        setQuoteItemId(inputData.quoteItemId);
        setFkCustomerId(inputData.fkCustomerId);
        setFkUserId(inputData.fkUserId);
        setReferenceNo(inputData.reference_no);
      } else if (location.state) {
        const {
          quoteId, quoteItemId, fkCustomerId,
          fkUserId, viewItem, reference_no
        } = location.state;

        setQuoteId(quoteId);
        setQuoteItemId(quoteItemId);
        setFkCustomerId(fkCustomerId);
        setViewItem(viewItem);
        setFkUserId(fkUserId);
        setReferenceNo(reference_no);
      }

      // When opened from "Add Predefined ... from My Library" the picker
      // navigates here with the template's full doorData in location.state.
      // No quoteItemId is set, so Save will create a fresh ItemQuote and
      // closing without saving creates nothing. We mirror the existing
      // edit-item path (setDoorData + setFormData + open the View Details
      // panel) so all sub-state setters propagate the template data.
      if (location.state?.libraryDoorData) {
        const tplDoorData = location.state.libraryDoorData;
        setDoorData(tplDoorData);
        setFormData(tplDoorData);
        setIsViewDetailsVisible(true);
      }
    };

    init();
  }, []);

  useEffect(() => {
    const loadEmail = async () => {
      const email = await getCustomerEmail(fkCustomerId);
      setCustomerEmail(email || "no customer email found");
    };

    if (fkCustomerId) {
      loadEmail();
    }
  }, [fkCustomerId]);

  // ============================================
  // ACTION HANDLERS
  // ============================================
  const handleFlipToggle = () => {
    setFlipped(prev => !prev);
  };

  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.05, 2));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.05, 0.2));
  };

  const handleSendEmail = async (recipient) => {
    setShowEmailDialog(true);
  };

  const handlePdfDownload = async () => {
    setIsLoading(true);
    try {
      // toPdfAuto picks server-side vs legacy based on USE_PDF_SERVER (constants.js).
      await toPdfAuto({
        container: ".view-details",
        filename: `${doorData?.frameData?.name || "PatioDoor"}`,
      });
    } finally {
      setIsLoading(false);
    }
  };

  // ============================================
  // SAVE QUOTE HANDLER
  // ============================================
  const handleSaveQuote = async (bSaveAndBack = false) => {
    try {
      if (bSaveAndBack) {
        const currentDoorDataJSON = JSON.stringify(doorData);
        const hasDataChanged = initialDoorDataJSON && (currentDoorDataJSON !== initialDoorDataJSON);

        if (hasDataChanged || !Number.isInteger(Number(quoteItemId))) {
          const userChoice = await confirmC("Do you want to save before exiting?");
          if (!userChoice) {
            navigate('/quotes', {
              state: { quoteId, quoteItemId }
            });
            return;
          }
        } else {
          navigate('/quotes', {
            state: { quoteId, quoteItemId }
          });
          return;
        }
      }

      const userId = getCookie('user_id');

      if (parseInt(userId) !== parseInt(fkUserId)) {
        await alertC("This quote item doesn't belong to you, you can't modify and save it.");
        if (bSaveAndBack) {
          navigate('/quotes', {
            state: { quoteId, quoteItemId }
          });
        }
        return;
      }

      const date = new Date();
      const formattedDate = date.toLocaleDateString('en-US', {
        year: '2-digit',
        month: '2-digit',
        day: '2-digit'
      });
      setLastDateSaved(formattedDate);
      doorData.document.orderDate = formattedDate;

      if (!quoteId && !quoteItemId) {
        await alertC('Create new Quote and NEW item');
      }

      if (quoteId && !quoteItemId && !newQuoteItemId) {
        const payload = {
          fkQuoteId: quoteId,
          doorData: doorData,
          itemType: itemPath,
        };
        const itemRow = await authFetchRequest(
          `/itemquotes/`,
          payload,
          "POST",
          { Authorization: `Bearer ${getCookie("access_token")}` }
        );
        setNewQuoteItemId(itemRow?.id);

        if (crmDefined && (crmUserId !== "NA")) {
          const product_id = await createCrmProduct({
            quoteId, itemRow, doorData,
            authHeaders: { Authorization: `Bearer ${getCookie("access_token")}` }
          });
          if (product_id) set_crm_product_id(product_id);
        }
      }

      if ((quoteId && quoteItemId) || (quoteId && newQuoteItemId)) {
        const payload = {
          fkQuoteId: quoteId,
          doorData: doorData
        };
        const currentQuoteItemId = quoteItemId || newQuoteItemId;
        const itemRow = await authFetchRequest(
          `/itemquotes/${currentQuoteItemId}`,
          payload,
          "PUT",
          { Authorization: `Bearer ${getCookie("access_token")}` }
        );

        if (crmDefined && (crmUserId !== "NA")) {
          await updateCrmProduct({
            quoteId, itemRow, doorData, crm_product_id,
            authHeaders: { Authorization: `Bearer ${getCookie("access_token")}` }
          });
        }
      }

      if (bSaveAndBack) {
        navigate('/quotes', {
          state: { quoteId, quoteItemId }
        });
      }
      return;

    } catch (err) {
      console.error("Failed to save quote and item quote:", err);
      alert("Failed to save.");
    }
  };

  const toggleViewDetails = () => {
    setIsViewDetailsVisible(prev => !prev);
  };

  const togglePrices = () => {
    setDisplayPrices(!displayPrices);
  };

  // ============================================
  // LOAD DATA
  // ============================================
  useEffect(() => {
    if (viewItem === 'readonly') {
      setIsDisabled(true);
      setDisplayPrices(true);
    }

    const fetchData = async () => {
      try {
        if (!quoteId || !quoteItemId) {
          console.warn("Missing quoteId or quoteItemId—skipping fetch.");
          return;
        }

        const data = await authFetch(`/itemquotes/${quoteItemId}`);

        if (!data?.doorData) {
          console.warn("No valid doorData found.");
          alert("No valid doorData found.");
          return;
        }

        setDoorData(data.doorData);

        if (crmDefined) {
          set_crm_product_id(data?.crm_product_id || null);
        }
        setFormData(data.doorData);
        setIsViewDetailsVisible(true);
        setHasLoaded(true);

      } catch (error) {
        console.error("Fetch error:", error);
        alert(`Error: ${error.message}`);
      }
    };

    fetchData();
  }, [quoteId, quoteItemId]);

  // ============================================
  // SET FORM DATA FROM LOADED DATA
  // Override this function or extend it for specific patio door fields
  // ============================================
  const setFormData = (doorData) => {
    setQuantityValue(doorData?.frameData?.quantity || "1");
    setDiscountValue(doorData?.discounts?.discount || "0");

    setPatioDoorTypeValue(doorData?.frameData?.patioDoorType || { value: config.DOOR_TYPE || "Sliding", label: doorTypeLabels[config.DOOR_TYPE] || "Sliding Door" });
    setUnitWidthValue(doorData?.frameData?.unitWidth || { value: config.UNIT_WIDTH || '58 3/4"', label: config.UNIT_WIDTH || '58 3/4"' });
    setUnitHeightValue(doorData?.frameData?.unitHeight || { value: config.UNIT_HEIGHT || '79 1/4"', label: config.UNIT_HEIGHT || '79 1/4"' });
    setOperationVentingValue(doorData?.frameData?.operationVenting || { value: config.OPERATION_VENTING || 'XO(OSLI)', label: config.OPERATION_VENTING || 'XO(OSLI)' });
    setPanelConfigValue(doorData?.frameData?.panelConfig || config.PANEL_CONFIG || "2");

    setGlassTypeValue(doorData?.glass?.glassType || { value: "", label: "" });
    setGlazingTypeValue(doorData?.glass?.glazingType || "Double");
    setGlassEnergyValue(doorData?.glass?.glassEnergy || { value: "LowE 272", label: "LowE 272" });
    setGlassStrengthValue(doorData?.glass?.glassStrength || { value: "", label: "" });
    setPetMeshValue(doorData?.glass?.petMesh || { value: "No", label: "No" });
    setKeyLockValue(doorData?.glass?.keyLock || { value: "No", label: "No" });
    setKickLockValue(doorData?.glass?.kickLock || { value: "No", label: "No" });
    setTopBoltValue(doorData?.glass?.topBolt || { value: "No", label: "No" });
    setLockFinishValue(doorData?.glass?.lockFinish || { value: "White", label: "White" });
    setLockSeriesValue(doorData?.glass?.lockSeries || { value: "", label: "" });

    setExteriorColorValue(doorData?.finish?.exteriorColor || { value: "White", label: "White" });
    setInteriorColorValue(doorData?.finish?.interiorColor || { value: "White", label: "White" });
    setColorFinishingInteriorValue(doorData?.finish?.colorFinishingInterior || "None");
    setColorFinishingExteriorValue(doorData?.finish?.colorFinishingExterior || "None");

    setInstallationTypeValue(doorData?.installation?.installationType || { value: "None", label: "None" });
    setExteriorTypeValue(doorData?.installation?.exteriorType || { value: "None", label: "None" });
    setBrickmoldSizeValue(doorData?.installation?.brickmoldSize || { value: "None", label: "None" });
    setBrickmoldColorValue(doorData?.installation?.brickmoldColor || { value: "#FFFFFF", label: "White" });
    setJambTypeValue(doorData?.installation?.jambType || { value: "None", label: "None" });
    setInteriorCasingValue(doorData?.installation?.interiorCasing || { value: "None", label: "None" });
    setInteriorCasingSizeValue(doorData?.installation?.interiorCasingSize || { value: "", label: "" });
    setFactoryAssemblyValue(doorData?.installation?.factoryAssembly || { value: "No", label: "No" });
    setSillNoseValue(doorData?.installation?.sillNose || { value: "None", label: "None" });


    setGrillSizeType1Value(doorData?.grills?.grillSizeType1 || { value: "", label: "" });
    setGrillPatternSimple1Value(doorData?.grills?.grillPatternSimple1 || { value: "", label: "" });
    setHorizontalGrills1Value(doorData?.grills?.horizontalGrills1 || "");
    setVerticalGrills1Value(doorData?.grills?.verticalGrills1 || "");
    setPrairieDividers1Value(doorData?.grills?.prairieDividers1 || { value: "standard", label: "Standard" });
    setCottageOptions1Value(doorData?.grills?.cottageOptions1 || { value: "1/4", label: "1/4" });
    setSpacerColor1Value(doorData?.grills?.spacerColor1 || { value: "", label: "" });

    setGrillSizeType2Value(doorData?.grills?.grillSizeType2 || { value: "", label: "" });
    setGrillPatternSimple2Value(doorData?.grills?.grillPatternSimple2 || { value: "", label: "" });
    setHorizontalGrills2Value(doorData?.grills?.horizontalGrills2 || "");
    setVerticalGrills2Value(doorData?.grills?.verticalGrills2 || "");
    setPrairieDividers2Value(doorData?.grills?.prairieDividers2 || { value: "standard", label: "Standard" });
    setCottageOptions2Value(doorData?.grills?.cottageOptions2 || { value: "1/4", label: "1/4" });
    setSpacerColor2Value(doorData?.grills?.spacerColor2 || { value: "", label: "" });

    setGrillSizeType3Value(doorData?.grills?.grillSizeType3 || { value: "", label: "" });
    setGrillPatternSimple3Value(doorData?.grills?.grillPatternSimple3 || { value: "", label: "" });
    setHorizontalGrills3Value(doorData?.grills?.horizontalGrills3 || "");
    setVerticalGrills3Value(doorData?.grills?.verticalGrills3 || "");
    setPrairieDividers3Value(doorData?.grills?.prairieDividers3 || { value: "standard", label: "Standard" });
    setCottageOptions3Value(doorData?.grills?.cottageOptions3 || { value: "1/4", label: "1/4" });
    setSpacerColor3Value(doorData?.grills?.spacerColor3 || { value: "", label: "" });

    setGrillSizeType4Value(doorData?.grills?.grillSizeType4 || { value: "", label: "" });
    setGrillPatternSimple4Value(doorData?.grills?.grillPatternSimple4 || { value: "", label: "" });
    setHorizontalGrills4Value(doorData?.grills?.horizontalGrills4 || "");
    setVerticalGrills4Value(doorData?.grills?.verticalGrills4 || "");
    setPrairieDividers4Value(doorData?.grills?.prairieDividers4 || { value: "standard", label: "Standard" });
    setCottageOptions4Value(doorData?.grills?.cottageOptions4 || { value: "1/4", label: "1/4" });
    setSpacerColor4Value(doorData?.grills?.spacerColor4 || { value: "", label: "" });

    setGrillColorInteriorValue(doorData?.grills?.grillColorInterior || { value: "", label: "" });
    setGrillColorExteriorValue(doorData?.grills?.grillColorExterior || { value: "", label: "" });

    setLocationTagValue(doorData?.notes?.LocationTag || doorData?.notes?.locationTag || "");
    setNotesValue(doorData?.notes?.notes || "");

    if (doorData?.additionalItems !== undefined){
      setCascadingComboValue1(doorData?.additionalItems?.CascadingCombo1 || "NA");
      setCascadingComboValue2(doorData?.additionalItems?.CascadingCombo2 || "NA");
      setCascadingComboValue3(doorData?.additionalItems?.CascadingCombo3 || "NA");
      setCascadingComboValue4(doorData?.additionalItems?.CascadingCombo4 || "NA");
      setCascadingComboValue5(doorData?.additionalItems?.CascadingCombo5 || "NA");
      const convertedCombos = Object.entries(doorData.additionalItems)
            .filter(([_, val]) => val !== "NA")
            .map(([id, value]) => ({ id, value }));
      setCascadingComboValue(convertedCombos);
    }
  };

  useEffect(() => {
    setCascadingComboValue1(CascadingComboValue[0]?.value || "NA");
    setCascadingComboValue2(CascadingComboValue[1]?.value || "NA");
    setCascadingComboValue3(CascadingComboValue[2]?.value || "NA");
    setCascadingComboValue4(CascadingComboValue[3]?.value || "NA");
    setCascadingComboValue5(CascadingComboValue[4]?.value || "NA");
  }, [CascadingComboValue]); // ✅ Ensures state updates when cascadingComboValue changes

  // ============================================
  // SECTION PROPS - Passed to control map generators
  // ============================================
  const sectionProps = {
    isDisabled,
    config,
    // Patio door specific
    patioDoorTypeValue, setPatioDoorTypeValue,
    unitWidthValue, setUnitWidthValue,
    unitHeightValue, setUnitHeightValue,
    operationVentingValue, setOperationVentingValue,
    panelConfigValue, setPanelConfigValue,
    // Glass
    glassTypeValue, setGlassTypeValue,
    glazingTypeValue, setGlazingTypeValue,
    glassEnergyValue, setGlassEnergyValue,
    glassStrengthValue, setGlassStrengthValue,
    petMeshValue, setPetMeshValue,
    keyLockValue, setKeyLockValue,
    kickLockValue, setKickLockValue,
    topBoltValue, setTopBoltValue,
    lockFinishValue, setLockFinishValue,
    lockSeriesValue, setLockSeriesValue,
    // Finish
    exteriorColorValue, setExteriorColorValue,
    interiorColorValue, setInteriorColorValue,
    colorFinishingInteriorValue, setColorFinishingInteriorValue,
    colorFinishingExteriorValue, setColorFinishingExteriorValue,
    // Grills - Group 1
    grillSizeType1Value, setGrillSizeType1Value,
    grillPatternSimple1Value, setGrillPatternSimple1Value,
    horizontalGrills1Value, setHorizontalGrills1Value,
    verticalGrills1Value, setVerticalGrills1Value,
    prairieDividers1Value, setPrairieDividers1Value,
    cottageOptions1Value, setCottageOptions1Value,
    spacerColor1Value, setSpacerColor1Value,
    // Grills - Group 2
    grillSizeType2Value, setGrillSizeType2Value,
    grillPatternSimple2Value, setGrillPatternSimple2Value,
    horizontalGrills2Value, setHorizontalGrills2Value,
    verticalGrills2Value, setVerticalGrills2Value,
    prairieDividers2Value, setPrairieDividers2Value,
    cottageOptions2Value, setCottageOptions2Value,
    spacerColor2Value, setSpacerColor2Value,
    // Grills - Group 3
    grillSizeType3Value, setGrillSizeType3Value,
    grillPatternSimple3Value, setGrillPatternSimple3Value,
    horizontalGrills3Value, setHorizontalGrills3Value,
    verticalGrills3Value, setVerticalGrills3Value,
    prairieDividers3Value, setPrairieDividers3Value,
    cottageOptions3Value, setCottageOptions3Value,
    spacerColor3Value, setSpacerColor3Value,
    // Grills - Group 4
    grillSizeType4Value, setGrillSizeType4Value,
    grillPatternSimple4Value, setGrillPatternSimple4Value,
    horizontalGrills4Value, setHorizontalGrills4Value,
    verticalGrills4Value, setVerticalGrills4Value,
    prairieDividers4Value, setPrairieDividers4Value,
    cottageOptions4Value, setCottageOptions4Value,
    spacerColor4Value, setSpacerColor4Value,
    // Grill Colors
    grillColorInteriorValue, setGrillColorInteriorValue,
    grillColorExteriorValue, setGrillColorExteriorValue,
    // Installation
    installationTypeValue, setInstallationTypeValue,
    exteriorTypeValue, setExteriorTypeValue,
    brickmoldSizeValue, setBrickmoldSizeValue,
    brickmoldColorValue, setBrickmoldColorValue,
    jambTypeValue, setJambTypeValue,
    interiorCasingValue, setInteriorCasingValue,
    interiorCasingSizeValue, setInteriorCasingSizeValue,
    factoryAssemblyValue, setFactoryAssemblyValue,
    sillNoseValue, setSillNoseValue,
    // Additional Items
    CascadingComboValue, setCascadingComboValue,
    // Notes
    locationTagValue, setLocationTagValue,
    notesValue, setNotesValue,
    // Discounts
    ctrl_discount_enabled,
    discountValue, setDiscountValue,
    quantityValue, setQuantityValue,
  };

  const hideOptions = (() => {
    // If Supply Only → hide nothing (show "None" & "Brickmold")
    if (installationTypeValue?.value === "Supply Only") {
      return ["Capping"];
    }

    // If Brick to Brick → hide None + Capping
    if (installationTypeValue?.value === "Brick to Brick") {
      return ["None", "Capping"];
    }

    // Default → hide Brickmold
    return ["Brickmold"];
  })();

  const jambHideOptions = (() => {
    // Brick to Brick → all displayed
    // Stud to Stud → all displayed
    if (installationTypeValue?.value === "Brick to Brick" || installationTypeValue?.value === "Stud to Stud") {
      return [];
    }
    // Retrofit → only None, 1/4" Round displayed
    if (installationTypeValue?.value === "Retrofit") {
      return ["Jamb Return Only", "Vinyl Jamb Ext", "Cove Moulding", "Door Stop", "Paint Grade Ext"];
    }
    // Supply Only → only None, Vinyl Jamb Ext, Jamb Return Only, Paint Grade Ext displayed
    if (installationTypeValue?.value === "Supply Only") {
      return ["1/4\" Round", "Cove Moulding", "Door Stop"];
    }
    return [];
  })();

  
  // ============================================
  // BUILD CONTROL MAP
  // Control map sections can be passed as props or imported
  // ============================================

  // Base controls defined in the template
  const baseControlMap = [
    {
      id: "PatioDoorType",
      component: (
        <PatioDoorType
          disabled={isDisabled}
          value={patioDoorTypeValue}
          onChange={(newValue) => setPatioDoorTypeValue(newValue)}
        />
      ),
      position: [1, 1],
      status: "active",
      expandableSectionId: "Configuration"
    },
    {
      id: "UnitWidth",
      component: (
        <UnitWidth
          disabled={isDisabled}
          value={unitWidthValue}
          patioDoorType={patioDoorTypeValue}
          onChange={(newValue) => setUnitWidthValue(newValue)}
        />
      ),
      position: [1, 1],
      status: "active",
      expandableSectionId: "SizingAndFrame"
    },
    {
      id: "UnitHeight",
      component: (
        <UnitHeight
          disabled={isDisabled}
          value={unitHeightValue}
          patioDoorType={patioDoorTypeValue}
          unitWidth={unitWidthValue}
          onChange={(newValue) => setUnitHeightValue(newValue)}
        />
      ),
      position: [1, 2],
      status: "active",
      expandableSectionId: "SizingAndFrame"
    },
    {
      id: "OperationVenting",
      component: (
        <OperationVenting
          disabled={isDisabled}
          value={operationVentingValue}
          patioDoorType={patioDoorTypeValue}
          onChange={(newValue) => setOperationVentingValue(newValue)}
        />
      ),
      position: [1, 3],
      status: "active",
      expandableSectionId: "SizingAndFrame"
    },
    // ---- Grills Group 1 (rows 1-3) - visible for all door types ----
    {
      id: "GrillSizeType1",
      component: (
        <GrillSizeType
          disabled={isDisabled}
          value={grillSizeType1Value}
          onChange={(newValue) => setGrillSizeType1Value(newValue)}
          appliesTo="patiodoors"
          label="Grill Size Type 1"
        />
      ),
      position: [1, 1],
      status: "active",
      expandableSectionId: "Grills"
    },
    ...(grillSizeType1Value?.value !== 'None' ? [
    {
      id: "GrillPatternSimple1",
      component: (
        <GrillPatternSimple
          disabled={isDisabled}
          showNone={false}
          value={grillPatternSimple1Value}
          onChange={(newValue) => setGrillPatternSimple1Value(newValue)}
          appliesTo="patiodoors"
          label="Grill Pattern 1"
        />
      ),
      position: [1, 2],
      status: "active",
      expandableSectionId: "Grills"
    },
    ...(['colonial', 'diamond', 'cottage'].includes(grillPatternSimple1Value?.value) ? [
    {
      id: "HorizontalGrills1",
      component: (
        <HorizontalGrills
          disabled={isDisabled}
          id="HorizontalGrills1"
          label="Horizontal lines 1"
          value={horizontalGrills1Value}
          onChange={(newValue) => { setHorizontalGrills1Value(newValue); }}
        />
      ),
      position: [2, 1],
      status: "active",
      expandableSectionId: "Grills"
    },
    {
      id: "VerticalGrills1",
      component: (
        <VerticalGrills
          disabled={isDisabled}
          id="VerticalGrills1"
          label="Vertical lines 1"
          value={verticalGrills1Value}
          onChange={(newValue) => { setVerticalGrills1Value(newValue); }}
        />
      ),
      position: [2, 2],
      status: "active",
      expandableSectionId: "Grills"
    },
    ] : []),
    ...(grillPatternSimple1Value?.value === 'prairie' ? [
    {
      id: "PrairieDividers1",
      component: (
        <PrairieDividers
          disabled={isDisabled}
          value={prairieDividers1Value}
          onChange={(newValue) => { setPrairieDividers1Value(newValue); }}
          label="Prairie Dividers 1"
        />
      ),
      position: [2, 2],
      status: "active",
      expandableSectionId: "Grills"
    },
    ] : []),
    ...(grillPatternSimple1Value?.value === 'cottage' ? [
    {
      id: "CottageOptions1",
      component: (
        <CottageOptions
          disabled={isDisabled}
          value={cottageOptions1Value}
          onChange={(newValue) => { setCottageOptions1Value(newValue); }}
          label="Cottage Options 1"
        />
      ),
      position: [2, 3],
      status: "active",
      expandableSectionId: "Grills"
    },
    ] : []),
    {
      id: "SpacerColor1",
      component: (
        <SpacerColor
          disabled={isDisabled}
          value={spacerColor1Value}
          onChange={(newValue) => { setSpacerColor1Value(newValue); }}
          label="Spacer Color 1"
        />
      ),
      position: [1, 3],
      status: "active",
      expandableSectionId: "Grills"
    },
    ] : []),

    // ---- Grills Group 2 (rows 4-6) - visible for all door types ----
    {
      id: "GrillSizeType2",
      component: (
        <GrillSizeType
          disabled={isDisabled}
          value={grillSizeType2Value}
          onChange={(newValue) => setGrillSizeType2Value(newValue)}
          appliesTo="patiodoors"
          label="Grill Size Type 2"
        />
      ),
      position: [4, 1],
      status: "active",
      expandableSectionId: "Grills"
    },
    ...(grillSizeType2Value?.value !== 'None' ? [
    {
      id: "GrillPatternSimple2",
      component: (
        <GrillPatternSimple
          disabled={isDisabled}
          showNone={false}
          value={grillPatternSimple2Value}
          onChange={(newValue) => setGrillPatternSimple2Value(newValue)}
          appliesTo="patiodoors"
          label="Grill Pattern 2"
        />
      ),
      position: [4, 2],
      status: "active",
      expandableSectionId: "Grills"
    },
    ...(['colonial', 'diamond', 'cottage'].includes(grillPatternSimple2Value?.value) ? [
    {
      id: "HorizontalGrills2",
      component: (
        <HorizontalGrills
          disabled={isDisabled}
          id="HorizontalGrills2"
          label="Horizontal lines 2"
          value={horizontalGrills2Value}
          onChange={(newValue) => { setHorizontalGrills2Value(newValue); }}
        />
      ),
      position: [5, 1],
      status: "active",
      expandableSectionId: "Grills"
    },
    {
      id: "VerticalGrills2",
      component: (
        <VerticalGrills
          disabled={isDisabled}
          id="VerticalGrills2"
          label="Vertical lines 2"
          value={verticalGrills2Value}
          onChange={(newValue) => { setVerticalGrills2Value(newValue); }}
        />
      ),
      position: [5, 2],
      status: "active",
      expandableSectionId: "Grills"
    },
    ] : []),
    ...(grillPatternSimple2Value?.value === 'prairie' ? [
    {
      id: "PrairieDividers2",
      component: (
        <PrairieDividers
          disabled={isDisabled}
          value={prairieDividers2Value}
          onChange={(newValue) => { setPrairieDividers2Value(newValue); }}
          label="Prairie Dividers 2"
        />
      ),
      position: [5, 2],
      status: "active",
      expandableSectionId: "Grills"
    },
    ] : []),
    ...(grillPatternSimple2Value?.value === 'cottage' ? [
    {
      id: "CottageOptions2",
      component: (
        <CottageOptions
          disabled={isDisabled}
          value={cottageOptions2Value}
          onChange={(newValue) => { setCottageOptions2Value(newValue); }}
          label="Cottage Options 2"
        />
      ),
      position: [5, 3],
      status: "active",
      expandableSectionId: "Grills"
    },
    ] : []),
    {
      id: "SpacerColor2",
      component: (
        <SpacerColor
          disabled={isDisabled}
          value={spacerColor2Value}
          onChange={(newValue) => { setSpacerColor2Value(newValue); }}
          label="Spacer Color 2"
        />
      ),
      position: [4, 3],
      status: "active",
      expandableSectionId: "Grills"
    },
    ] : []),

    // ---- Grills Group 3 (rows 7-9) - visible for Triple & Quad ----
    ...(patioDoorTypeValue?.value === 'TripleSliding' || patioDoorTypeValue?.value === 'QuadSliding' ? [
    {
      id: "GrillSizeType3",
      component: (
        <GrillSizeType
          disabled={isDisabled}
          value={grillSizeType3Value}
          onChange={(newValue) => setGrillSizeType3Value(newValue)}
          appliesTo="patiodoors"
          label="Grill Size Type 3"
        />
      ),
      position: [7, 1],
      status: "active",
      expandableSectionId: "Grills"
    },
    ...(grillSizeType3Value?.value !== 'None' ? [
    {
      id: "GrillPatternSimple3",
      component: (
        <GrillPatternSimple
          disabled={isDisabled}
          showNone={false}
          value={grillPatternSimple3Value}
          onChange={(newValue) => setGrillPatternSimple3Value(newValue)}
          appliesTo="patiodoors"
          label="Grill Pattern 3"
        />
      ),
      position: [7, 2],
      status: "active",
      expandableSectionId: "Grills"
    },
    ...(['colonial', 'diamond', 'cottage'].includes(grillPatternSimple3Value?.value) ? [
    {
      id: "HorizontalGrills3",
      component: (
        <HorizontalGrills
          disabled={isDisabled}
          id="HorizontalGrills3"
          label="Horizontal lines 3"
          value={horizontalGrills3Value}
          onChange={(newValue) => { setHorizontalGrills3Value(newValue); }}
        />
      ),
      position: [8, 1],
      status: "active",
      expandableSectionId: "Grills"
    },
    {
      id: "VerticalGrills3",
      component: (
        <VerticalGrills
          disabled={isDisabled}
          id="VerticalGrills3"
          label="Vertical lines 3"
          value={verticalGrills3Value}
          onChange={(newValue) => { setVerticalGrills3Value(newValue); }}
        />
      ),
      position: [8, 2],
      status: "active",
      expandableSectionId: "Grills"
    },
    ] : []),
    ...(grillPatternSimple3Value?.value === 'prairie' ? [
    {
      id: "PrairieDividers3",
      component: (
        <PrairieDividers
          disabled={isDisabled}
          value={prairieDividers3Value}
          onChange={(newValue) => { setPrairieDividers3Value(newValue); }}
          label="Prairie Dividers 3"
        />
      ),
      position: [8, 2],
      status: "active",
      expandableSectionId: "Grills"
    },
    ] : []),
    ...(grillPatternSimple3Value?.value === 'cottage' ? [
    {
      id: "CottageOptions3",
      component: (
        <CottageOptions
          disabled={isDisabled}
          value={cottageOptions3Value}
          onChange={(newValue) => { setCottageOptions3Value(newValue); }}
          label="Cottage Options 3"
        />
      ),
      position: [8, 3],
      status: "active",
      expandableSectionId: "Grills"
    },
    ] : []),
    {
      id: "SpacerColor3",
      component: (
        <SpacerColor
          disabled={isDisabled}
          value={spacerColor3Value}
          onChange={(newValue) => { setSpacerColor3Value(newValue); }}
          label="Spacer Color 3"
        />
      ),
      position: [7, 3],
      status: "active",
      expandableSectionId: "Grills"
    },
    ] : []),
    ] : []),

    // ---- Grills Group 4 (rows 10-12) - visible for Quad only ----
    ...(patioDoorTypeValue?.value === 'QuadSliding' ? [
    {
      id: "GrillSizeType4",
      component: (
        <GrillSizeType
          disabled={isDisabled}
          value={grillSizeType4Value}
          onChange={(newValue) => setGrillSizeType4Value(newValue)}
          appliesTo="patiodoors"
          label="Grill Size Type 4"
        />
      ),
      position: [10, 1],
      status: "active",
      expandableSectionId: "Grills"
    },
    ...(grillSizeType4Value?.value !== 'None' ? [
    {
      id: "GrillPatternSimple4",
      component: (
        <GrillPatternSimple
          disabled={isDisabled}
          showNone={false}
          value={grillPatternSimple4Value}
          onChange={(newValue) => setGrillPatternSimple4Value(newValue)}
          appliesTo="patiodoors"
          label="Grill Pattern 4"
        />
      ),
      position: [10, 2],
      status: "active",
      expandableSectionId: "Grills"
    },
    ...(['colonial', 'diamond', 'cottage'].includes(grillPatternSimple4Value?.value) ? [
    {
      id: "HorizontalGrills4",
      component: (
        <HorizontalGrills
          disabled={isDisabled}
          id="HorizontalGrills4"
          label="Horizontal lines 4"
          value={horizontalGrills4Value}
          onChange={(newValue) => { setHorizontalGrills4Value(newValue); }}
        />
      ),
      position: [11, 1],
      status: "active",
      expandableSectionId: "Grills"
    },
    {
      id: "VerticalGrills4",
      component: (
        <VerticalGrills
          disabled={isDisabled}
          id="VerticalGrills4"
          label="Vertical lines 4"
          value={verticalGrills4Value}
          onChange={(newValue) => { setVerticalGrills4Value(newValue); }}
        />
      ),
      position: [11, 2],
      status: "active",
      expandableSectionId: "Grills"
    },
    ] : []),
    ...(grillPatternSimple4Value?.value === 'prairie' ? [
    {
      id: "PrairieDividers4",
      component: (
        <PrairieDividers
          disabled={isDisabled}
          value={prairieDividers4Value}
          onChange={(newValue) => { setPrairieDividers4Value(newValue); }}
          label="Prairie Dividers 4"
        />
      ),
      position: [11, 2],
      status: "active",
      expandableSectionId: "Grills"
    },
    ] : []),
    ...(grillPatternSimple4Value?.value === 'cottage' ? [
    {
      id: "CottageOptions4",
      component: (
        <CottageOptions
          disabled={isDisabled}
          value={cottageOptions4Value}
          onChange={(newValue) => { setCottageOptions4Value(newValue); }}
          label="Cottage Options 4"
        />
      ),
      position: [11, 3],
      status: "active",
      expandableSectionId: "Grills"
    },
    ] : []),
    {
      id: "SpacerColor4",
      component: (
        <SpacerColor
          disabled={isDisabled}
          value={spacerColor4Value}
          onChange={(newValue) => { setSpacerColor4Value(newValue); }}
          label="Spacer Color 4"
        />
      ),
      position: [10, 3],
      status: "active",
      expandableSectionId: "Grills"
    },
    ] : []),
    ] : []),
    {
      id: "GlazingType",
      component: (
        <GlazingType
          disabled={isDisabled}
          value={glazingTypeValue}
          onChange={(newValue) => setGlazingTypeValue(newValue)}
          label="# of Glass Panes"
          appliesTo="patiodoors"
        />
      ),
      position: [1, 1],
      status: "active",
      expandableSectionId: "Glass"
    },
    {
      id: "GlassEnergy",
      component: (
        <GlassEnergy
          disabled={isDisabled}
          value={glassEnergyValue}
          onChange={(newValue) => setGlassEnergyValue(newValue)}
          label="Glass Coatings"
          appliesTo="patiodoors"
        />
      ),
      position: [1, 2],
      status: "active",
      expandableSectionId: "Glass"
    },
    {
      id: "GlassStrength",
      component: (
        <GlassStrength
          disabled={isDisabled}
          value={glassStrengthValue}
          onChange={(newValue) => setGlassStrengthValue(newValue)}
          appliesTo="patiodoors"
        />
      ),
      position: [1, 3],
      status: "active",
      expandableSectionId: "Glass"
    },
    {
      id: "PetMesh",
      component: (
        <YesNo
          disabled={isDisabled}
          id="PetMesh"
          label="Pet Mesh"
          value={petMeshValue}
          onChange={(newValue) => setPetMeshValue(newValue)}
        />
      ),
      position: [2, 1],
      status: "active",
      expandableSectionId: "Glass"
    },
    {
      id: "LockSeries",
      component: (
        <LockSeries
          disabled={isDisabled}
          id="LockSeries"
          label="Lock Series"
          value={lockSeriesValue}
          onChange={(newValue) => setLockSeriesValue(newValue)}
        />
      ),
      position: [2, 2],
      status: "active",
      expandableSectionId: "Glass"
    },
    {
      id: "LockFinish",
      component: (
        <LockFinish
          disabled={isDisabled}
          id="LockFinish"
          label="Lock Finish"
          value={lockFinishValue}
          onChange={(newValue) => setLockFinishValue(newValue)}
        />
      ),
      position: [2, 3],
      status: "active",
      expandableSectionId: "Glass"
    },
    {
      id: "KeyLock",
      component: (
        <YesNo
          disabled={isDisabled}
          id="KeyLock"
          label="Key Lock"
          value={keyLockValue}
          onChange={(newValue) => setKeyLockValue(newValue)}
        />
      ),
      position: [3, 1],
      status: "active",
      expandableSectionId: "Glass"
    },
    {
      id: "KickLock",
      component: (
        <YesNo
          disabled={isDisabled}
          id="KickLock"
          label="Kick Lock"
          value={kickLockValue}
          onChange={(newValue) => setKickLockValue(newValue)}
        />
      ),
      position: [3, 2],
      status: "active",
      expandableSectionId: "Glass"
    },
    {
      id: "TopBolt",
      component: (
        <YesNo
          disabled={isDisabled}
          id="TopBolt"
          label="Top Bolt"
          value={topBoltValue}
          onChange={(newValue) => setTopBoltValue(newValue)}
        />
      ),
      position: [3, 3],
      status: "active",
      expandableSectionId: "Glass"
    },
    {
      id: "WinColorFull2",
      component: (
        <WinColorFull
          controlId={2}
          disabled={isDisabled}
          value={interiorColorValue}
          onChange={(newValue) => { setInteriorColorValue(newValue); }}
          label="Color Interior"
          color={interiorColorValue}
          colorType="internal"
          appliesTo="patiodoors"
        />
      ),
      position: [1, 1],
      status: "active",
      expandableSectionId: "Finish"
    },
    {
      id: "WinColorFull1",
      component: (
        <WinColorFull
          controlId={1}
          disabled={isDisabled}
          value={exteriorColorValue}
          onChange={(newValue) => { setExteriorColorValue(newValue); }}
          label="Color Exterior"
          color={exteriorColorValue}
          colorType="external"
          appliesTo="patiodoors"
        />
      ),
      position: [2, 1],
      status: "active",
      expandableSectionId: "Finish"
    },
    {
      id: "ColorFinishingInterior",
      component: (
        <ColorFinishing
          disabled={isDisabled}
          id="ColorFinishingInterior"
          label="Color Finishing Interior"
          value={colorFinishingInteriorValue}
          onChange={(newValue) => { setColorFinishingInteriorValue(newValue); }}
        />
      ),
      position: [1, 2],
      status: "active",
      expandableSectionId: "Finish"
    },
    {
      id: "ColorFinishingExterior",
      component: (
        <ColorFinishing
          disabled={isDisabled}
          id="ColorFinishingExterior"
          label="Color Finishing Exterior"
          value={colorFinishingExteriorValue}
          onChange={(newValue) => { setColorFinishingExteriorValue(newValue); }}
        />
      ),
      position: [2, 2],
      status: "active",
      expandableSectionId: "Finish"
    },
    ...((true && grillSizeType1Value?.value !== 'None') || 
        (true && grillSizeType2Value?.value !== 'None') || 
        (doorData.frameData.sectionNumber >=3 && grillSizeType3Value?.value !== 'None') || 
        (doorData.frameData.sectionNumber >=4 && grillSizeType4Value?.value !== 'None') ? [
    {
      id: "GrillColorInterior",
      component: (
        <GrillColor
          controlId={1}
          disabled={isDisabled}
          value={grillColorInteriorValue}
          onChange={(newValue) => setGrillColorInteriorValue(newValue)}
          label="Grill Color Interior"
          colorType="interior"
          appliesTo="patiodoors"
        />
      ),
      position: [3, 1],
      status: "active",
      expandableSectionId: "Finish"
    },
    {
      id: "GrillColorExterior",
      component: (
        <GrillColor
          controlId={2}
          disabled={isDisabled}
          value={grillColorExteriorValue}
          onChange={(newValue) => setGrillColorExteriorValue(newValue)}
          label="Grill Color Exterior"
          colorType="exterior"
          appliesTo="patiodoors"
        />
      ),
      position: [3, 2],
      status: "active",
      expandableSectionId: "Finish"
    },
    ] : []),

    // ---- Installation Section ----
    { id: "InstallationType", component: (
      <InstallationType
        disabled={isDisabled} id="InstallationType" showPics={false}
        value={installationTypeValue}
        onChange={(newValue) => setInstallationTypeValue(newValue)}
        appliesTo="patiodoors"
      />
    ), position: [1, 1], status: "active", expandableSectionId: "Installation" },

    ...(installationTypeValue?.value !== "None" ? [
    { id: "ExteriorType", component: (
      <ExteriorType
        disabled={isDisabled} id="ExteriorType" label="Exterior Finish Type" showPics={false}
        hideOptions1={installationTypeValue?.value === "Brick to Brick" ? ["None", "Capping"] : ["Brickmold"]}
        hideOptions={hideOptions}
        value={exteriorTypeValue}
        onChange={(newValue) => setExteriorTypeValue(newValue)}
        appliesTo="patiodoors"
      />
    ), position: [1, 2], status: "active", expandableSectionId: "Installation" },
    ] : []),

    ...(installationTypeValue?.value === "Brick to Brick" ? [
    { id: "BrickmoldSize", component: (
      <BrickmoldSize
        disabled={isDisabled} id="BrickmoldSize" showPics={false}
        value={brickmoldSizeValue}
        onChange={(newValue) => setBrickmoldSizeValue(newValue)}
        appliesTo="patiodoors"
      />
    ), position: [2, 1], status: "active", expandableSectionId: "Installation" },
    { id: "BrickmoldColor", component: (
      <BrickmoldColor controlId={2}
        disabled={isDisabled}
        value={brickmoldColorValue}
        onChange={(newValue) => setBrickmoldColorValue(newValue)}
        appliesTo="patiodoors"
      />
    ), position: [2, 2], status: "active", expandableSectionId: "Installation" },
    //---
    {
      id: "SillNose",
      component: (
        <SillNose
          disabled={isDisabled}
          id="SillNose"
          label="Sill Nose"
          value={sillNoseValue}
          onChange={(newValue) => setSillNoseValue(newValue)}
        />
      ),
      position: [4, 1],
      status: "active",
      expandableSectionId: "Installation"
    },
    //----
    ] : []),

    ...(installationTypeValue?.value !== "None" ? [
    {
      id: "FactoryAssembly",
      component: (
        <YesNo
          disabled={isDisabled}
          id="FactoryAssembly"
          label="Factory Assembly"
          value={factoryAssemblyValue}
          onChange={(newValue) => setFactoryAssemblyValue(newValue)}
        />
      ),
      position: [4, 2],
      status: "active",
      expandableSectionId: "Installation"
    },
    { id: "JambType", component: (
      <JambType
        disabled={isDisabled} id="JambType" showPics={false}
        hideOptions={jambHideOptions}
        value={jambTypeValue}
        onChange={(newValue) => setJambTypeValue(newValue)}
        appliesTo="patiodoors"
      />
    ), position: [3, 1], status: "active", expandableSectionId: "Installation" },
    ] : []),

    ...(installationTypeValue?.value !== "None" && installationTypeValue?.value !== "Retrofit" ? [
    { id: "InteriorCasing", component: (
      <InteriorCasing
        disabled={isDisabled} id="InteriorCasing" showPics={false}
        value={interiorCasingValue}
        onChange={(newValue) => setInteriorCasingValue(newValue)}
        appliesTo="patiodoors"
      />
    ), position: [3, 2], status: "active", expandableSectionId: "Installation" },
    ] : []),

    ...(installationTypeValue?.value !== "None" && false && installationTypeValue?.value !== "Retrofit" && interiorCasingValue?.value !== "None" ? [
    { id: "InteriorCasingSize", component: (
      <InteriorCasingSize
        disabled={isDisabled} id="InteriorCasingSize" showPics={false}
        value={interiorCasingSizeValue}
        onChange={(newValue) => setInteriorCasingSizeValue(newValue)}
        appliesTo="patiodoors"
      />
    ), position: [3, 3], status: "active", expandableSectionId: "Installation" },
    ] : []),

    // AdditionalItems section starts ...
    {
      id: "CascadingCombo", component: <CascadingCombo
        disabled={isDisabled} label = 'item' value={CascadingComboValue}
        onChange={(newValue) => { setCascadingComboValue(newValue); }}
      />,  // ✅ Render your component inside the expandable panel
      position: [1, 1], status: "active", expandableSectionId: "AdditionalItems"
    },
    // Additional Items ends ...

    // ---- Notes Section ----
    // notes section starts ...
    { id: "LocationTag", component: <LocationTag
      disabled={isDisabled}
      value={locationTagValue}
      onChange={(newValue) =>
      { setLocationTagValue(newValue); }
    }/>, position: [1, 1], status: "active", expandableSectionId: "Notes" },

    { id: "Notes",  component: <Notes
      disabled={isDisabled}
      value={notesValue}
      onChange={(newValue) =>
      { setNotesValue(newValue); }
    }/>, position: [2, "1 / span 3"], status: "active", expandableSectionId: "Notes" },
    // notes ends ...

    // discount section starts ...

    // ✅ Conditionally add Discount only if enabled
    ...(ctrl_discount_enabled === 1
      ? [
          {
            id: "Discount",
            component: (
              <Discount
                disabled={isDisabled}
                label="Discount %"
                value={discountValue}
                onChange={(newValue) => setDiscountValue(newValue)}
              />
            ),
            position: [1, 1],
            status: "active",
            expandableSectionId: "Discounts",
          },
        ]
      : []),


    { id: "Quantity", component: (
      <Quantity
        disabled={isDisabled}
        value={quantityValue}
        onChange={(newValue) => { setQuantityValue(newValue); }}
      />
    ), position: [1, 1], status: "active", expandableSectionId: "Discounts" },
    //discount ends...
  ];

  // Merge base controls with any additional controls from controlMapSections
  const additionalControls = controlMapSections.length > 0
    ? controlMapSections.flatMap(getSection => getSection(sectionProps)).filter(Boolean)
    : [];

  const controlMap = [...baseControlMap, ...additionalControls];

  
  // ============================================
  // DOOR DATA BUILDING
  // ============================================
  doorData.calc = doorData.calc || {};
  doorData.frameData = doorData.frameData || {};
  doorData.glass = doorData.glass || {};
  doorData.finish = doorData.finish || {};
  doorData.grills = doorData.grills || {};
  doorData.notes = doorData.notes || {};
  doorData.installation = doorData.installation || {};
  doorData.additionalItems = doorData.additionalItems || {};
  doorData.discounts = doorData.discounts || {};
  doorData.document = doorData.document || {};

  doorData.document.type = documentType;
  doorData.document.orderNumber = `${quoteId}/${quoteItemId || newQuoteItemId || 'new item'}`;
  doorData.document.quoteId = quoteId;
  doorData.document.quoteItemId = quoteItemId;
  doorData.document.reference = `${reference_no}`;
  doorData.document.orderDate = `${lastDateSaved}`;

  doorData.quoteTemplate = quoteTemplate;
  doorData.priceRules = priceRules;
  doorData.displayPrices = displayPrices;

  // Patio door specific data
  doorData.frameData.selectedDoor = {}; //for compartibility with doors
  doorData.frameData.name = patioDoorTypeValue.label || "NA";
  doorData.frameData.selectedDoor.name = doorData.frameData.name;
  doorData.frameData.patioDoorType = patioDoorTypeValue;
  doorData.frameData.sectionNumber = 2;
  if (patioDoorTypeValue?.value === 'TripleSliding') {
    doorData.frameData.sectionNumber = 3;
  }
  if (patioDoorTypeValue?.value === 'QuadSliding') {
    doorData.frameData.sectionNumber = 4;
  }
  

  doorData.frameData.unitWidth = unitWidthValue;
  doorData.frameData.unitWidth_d = formatToDecimal(unitWidthValue?.value || '0');
  doorData.frameData.unitHeight = unitHeightValue;
  doorData.frameData.unitHeight_d = formatToDecimal(unitHeightValue?.value || '0');
  doorData.frameData.operationVenting = operationVentingValue;
  doorData.frameData.panelConfig = panelConfigValue;
  doorData.frameData.quantity = quantityValue;
  doorData.glass.glassType = glassTypeValue;
  doorData.glass.glazingType = glazingTypeValue;
  doorData.glass.glassEnergy = glassEnergyValue;
  doorData.glass.glassStrength = glassStrengthValue;
  doorData.glass.petMesh = petMeshValue;
  doorData.glass.keyLock = keyLockValue;
  doorData.glass.kickLock = kickLockValue;
  doorData.glass.topBolt = topBoltValue;
  doorData.glass.lockFinish = lockFinishValue;
  doorData.glass.lockSeries = lockSeriesValue;

  doorData.finish.exteriorColor = exteriorColorValue;
  doorData.finish.interiorColor = interiorColorValue;
  doorData.finish.colorFinishingInterior = colorFinishingInteriorValue;
  doorData.finish.colorFinishingExterior = colorFinishingExteriorValue;

  doorData.grills.grillSizeType1 = grillSizeType1Value;
  doorData.grills.grillPatternSimple1 = grillPatternSimple1Value;
  doorData.grills.horizontalGrills1 = horizontalGrills1Value;
  doorData.grills.verticalGrills1 = verticalGrills1Value;
  doorData.grills.prairieDividers1 = prairieDividers1Value;
  doorData.grills.cottageOptions1 = cottageOptions1Value;
  doorData.grills.spacerColor1 = spacerColor1Value;

  doorData.grills.grillSizeType2 = grillSizeType2Value;
  doorData.grills.grillPatternSimple2 = grillPatternSimple2Value;
  doorData.grills.horizontalGrills2 = horizontalGrills2Value;
  doorData.grills.verticalGrills2 = verticalGrills2Value;
  doorData.grills.prairieDividers2 = prairieDividers2Value;
  doorData.grills.cottageOptions2 = cottageOptions2Value;
  doorData.grills.spacerColor2 = spacerColor2Value;

  doorData.grills.grillSizeType3 = grillSizeType3Value;
  doorData.grills.grillPatternSimple3 = grillPatternSimple3Value;
  doorData.grills.horizontalGrills3 = horizontalGrills3Value;
  doorData.grills.verticalGrills3 = verticalGrills3Value;
  doorData.grills.prairieDividers3 = prairieDividers3Value;
  doorData.grills.cottageOptions3 = cottageOptions3Value;
  doorData.grills.spacerColor3 = spacerColor3Value;

  doorData.grills.grillSizeType4 = grillSizeType4Value;
  doorData.grills.grillPatternSimple4 = grillPatternSimple4Value;
  doorData.grills.horizontalGrills4 = horizontalGrills4Value;
  doorData.grills.verticalGrills4 = verticalGrills4Value;
  doorData.grills.prairieDividers4 = prairieDividers4Value;
  doorData.grills.cottageOptions4 = cottageOptions4Value;
  doorData.grills.spacerColor4 = spacerColor4Value;

  doorData.grills.grillColorInterior = grillColorInteriorValue;
  doorData.grills.grillColorExterior = grillColorExteriorValue;

  // drawGrills() compatibility keys
  // grillSizeType: value="1_georgian", label='1" Georgian'
  // drawGrills expects: grillType.value for "None" check, grillsSize for hashSize lookup,
  //                     grillsPattern.value for pattern matching (capitalized: 'Colonial', 'Prairie', etc.)
  const parseGrillSizeType = (val) => {
    const v = val?.value || 'None';
    if (v === 'None') return { type: { value: 'None', label: 'None' }, size: '5/8"' };
    const lbl = val?.label || '';
    const sizeMatch = lbl.match(/^[\d\s\/]+"/) ;
    const size = sizeMatch ? sizeMatch[0].trim() : '5/8"';
    const type = lbl.replace(sizeMatch?.[0] || '', '').trim() || v;
    return { type: { value: type, label: type }, size };
  };
  // grillPatternSimple: value="colonial", label="Colonial"
  // drawGrills compares with capitalized strings, so use label as value
  const patternForDraw = (val) => {
    if (!val || val.value === 'None' || val.value === '') return { value: 'None', label: 'None' };
    return { value: val.label || val.value, label: val.label || val.value };
  };

  const gst1 = parseGrillSizeType(grillSizeType1Value);
  doorData.grills.grillType1 = gst1.type;
  doorData.grills.grillsPattern1 = patternForDraw(grillPatternSimple1Value);
  doorData.grills.grillsSize1 = gst1.size;
  doorData.grills.glassDividersVertical1 = verticalGrills1Value || 0;
  doorData.grills.glassDividersHorizontal1 = horizontalGrills1Value || 0;

  const gst2 = parseGrillSizeType(grillSizeType2Value);
  doorData.grills.grillType2 = gst2.type;
  doorData.grills.grillsPattern2 = patternForDraw(grillPatternSimple2Value);
  doorData.grills.grillsSize2 = gst2.size;
  doorData.grills.glassDividersVertical2 = verticalGrills2Value || 0;
  doorData.grills.glassDividersHorizontal2 = horizontalGrills2Value || 0;

  const gst3 = parseGrillSizeType(grillSizeType3Value);
  doorData.grills.grillType3 = gst3.type;
  doorData.grills.grillsPattern3 = patternForDraw(grillPatternSimple3Value);
  doorData.grills.grillsSize3 = gst3.size;
  doorData.grills.glassDividersVertical3 = verticalGrills3Value || 0;
  doorData.grills.glassDividersHorizontal3 = horizontalGrills3Value || 0;

  const gst4 = parseGrillSizeType(grillSizeType4Value);
  doorData.grills.grillType4 = gst4.type;
  doorData.grills.grillsPattern4 = patternForDraw(grillPatternSimple4Value);
  doorData.grills.grillsSize4 = gst4.size;
  doorData.grills.glassDividersVertical4 = verticalGrills4Value || 0;
  doorData.grills.glassDividersHorizontal4 = horizontalGrills4Value || 0;

  doorData.grills.interiorFinish = grillColorInteriorValue || { value: "white", label: "white" };
  doorData.grills.exteriorFinish = grillColorExteriorValue || { value: "white", label: "white" };

  doorData.installation.installationType   = installationTypeValue;
  doorData.installation.exteriorType       = exteriorTypeValue;
  doorData.installation.brickmoldSize      = brickmoldSizeValue;
  doorData.installation.brickmoldColor     = brickmoldColorValue || { value: "#FFFFFF", label: "White" };
  doorData.installation.jambType           = jambTypeValue;
  doorData.installation.interiorCasing     = interiorCasingValue;
  doorData.installation.interiorCasingSize = interiorCasingSizeValue;
  doorData.installation.sillNose           = sillNoseValue;
  doorData.installation.factoryAssembly     = factoryAssemblyValue;


  doorData.notes.LocationTag = locationTagValue;
  doorData.notes.notes = notesValue;

  CascadingComboValue.forEach(({ id, value }) => {
    doorData.additionalItems[id] = value;
  });
  requiredKeys.forEach(key => {
    if (!doorData.additionalItems.hasOwnProperty(key)) {
        doorData.additionalItems[key] = "NA";
    }
  });

  doorData.discounts.discount = discountValue;

  // Calculate square footage and perimeter for each panel
  const calcSquares = () => {
    const pdType = patioDoorTypeValue?.value || 'Sliding';
    const pdSections = pdType === 'QuadSliding' ? 4 : pdType === 'TripleSliding' ? 3 : 2;
    const totalW = parseFloat(doorData.frameData.unitWidth_d) || 0;
    const totalH = parseFloat(doorData.frameData.unitHeight_d) || 0;
    const panelW = totalW / pdSections;

    let totalSq = 0;
    for (let i = 1; i <= 4; i++) {
      if (i <= pdSections && panelW > 0 && totalH > 0) {
        const sq = (panelW * totalH) / 144;
        doorData.frameData[`square${i}`] = sq.toFixed(2);
        totalSq += sq;
      } else {
        doorData.frameData[`square${i}`] = "0.00";
      }
    }
    doorData.frameData.totalSquare = totalSq.toFixed(2);
  };

  const calcPerimeters = () => {
    const pdType = patioDoorTypeValue?.value || 'Sliding';
    const pdSections = pdType === 'QuadSliding' ? 4 : pdType === 'TripleSliding' ? 3 : 2;
    const totalW = parseFloat(doorData.frameData.unitWidth_d) || 0;
    const totalH = parseFloat(doorData.frameData.unitHeight_d) || 0;
    const panelW = totalW / pdSections;

    let totalPerim = 0;
    for (let i = 1; i <= 4; i++) {
      if (i <= pdSections && panelW > 0 && totalH > 0) {
        const perim = (2 * (panelW + totalH)) / 12;
        doorData.frameData[`perimeter${i}`] = perim.toFixed(2);
        totalPerim += perim;
      } else {
        doorData.frameData[`perimeter${i}`] = "0.00";
      }
    }
    doorData.frameData.totalPerimeter = totalPerim.toFixed(2);
  };

  calcSquares();
  calcPerimeters();

  doorData.flipped = !flipped;

  // ============================================
  // CHANGE DETECTION
  // ============================================
  if (hasLoaded && initialDoorDataJSON === null && doorData && Object.keys(doorData).length > 0) {
    setInitialDoorDataJSON(JSON.stringify(doorData));
  }

  const doorDataCopy = { ...doorData };
  const doorDataCopy2 = { ...doorData };
  let moreOffsetX = flipped ? 40 : 0;

  // ============================================
  // SECTION NAVIGATION
  // ============================================
  const scrollToSection = (sectionId) => {
    setSelectedSectionId(sectionId);
    setExpandedSections(prev => ({ ...prev, [sectionId]: true }));
    setTimeout(() => {
      const sectionElement = document.querySelector(`[data-section-id="${sectionId}"]`);
      if (sectionElement) {
        sectionElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }
    }, 0);
  };

  const handleSectionExpandChange = (sectionId, isExpanded) => {
    setExpandedSections(prev => ({ ...prev, [sectionId]: isExpanded }));
  };

  const isSectionVisible = (sectionId) => {
    if (!showOnlySelectedSection) return true;
    return selectedSectionId === sectionId;
  };

  const getForceExpanded = (sectionId) => {
    if (showOnlySelectedSection && selectedSectionId === sectionId) {
      return true;
    }
    return null;
  };

  // ============================================
  // CALC DATA CALLBACK
  // ============================================
  const updateCalcData = (newCalc) => {
    setDoorData(prev => ({
      ...prev,
      calc: {
        ...prev.calc,
        ...newCalc,
      },
    }));
  };

  // ============================================
  // SECTION DEFINITIONS FOR PATIO DOORS
  // ============================================
  const sections = [
    { id: 'Configuration', title: '🛠️ Configuration' },
    { id: 'SizingAndFrame', title: '📐 Sizing & Frame' },
    { id: 'Grills', title: '▦ Grills' },
    { id: 'Glass', title: '🔲 Glass' },
    { id: 'Finish', title: '🎨 Finish' },
    { id: 'AdditionalItems', title: '➕ Additional Items' },
    { id: 'Installation', title: '🔧 Installation' },
    { id: 'Notes', title: '📝 Notes' },
    { id: 'Discounts', title: '🏷️ Quantity' },
  ];

  // No sections are disabled for now
  const disabledSections = {};
  const hiddenSections = ['SizingAndFrameTop', 'GrillsTop'];

  // ============================================
  // RENDER
  // ============================================
  return (
    <div className='entire-container' style={showTopBar ? { marginTop: '-1.5rem' } : undefined}>
      {/* Floating Nav Menu */}
      {enableFloatingNavMenu && !showTopBar && useSmallNavMenu && (
        <SmallFloatingNavMenu
          onNavigate={scrollToSection}
          showOnlySelected={showOnlySelectedSection}
          toggleShowOnlySelected={() => setShowOnlySelectedSection(prev => !prev)}
          selectedSectionId={selectedSectionId}
          disabledSections={disabledSections}
          hiddenSections={hiddenSections}
          onToggleSize={() => setUseSmallNavMenu(false)}
        />
      )}
      {enableFloatingNavMenu && !showTopBar && !useSmallNavMenu && (
        <LargeFloatingNavMenu
          onNavigate={scrollToSection}
          showOnlySelected={showOnlySelectedSection}
          toggleShowOnlySelected={() => setShowOnlySelectedSection(prev => !prev)}
          selectedSectionId={selectedSectionId}
          disabledSections={disabledSections}
          hiddenSections={hiddenSections}
          onToggleSize={() => setUseSmallNavMenu(true)}
        />
      )}

      {/* Floating Toolbar Menu */}
      {enableFloatingToolbarMenu && !showTopBar && (
        <SmallFloatingToolbarMenu
          isViewDetailsVisible={isViewDetailsVisible}
          toggleViewDetails={toggleViewDetails}
          displayPrices={displayPrices}
          togglePrices={togglePrices}
          isLoading={isLoading}
          handlePdfDownload={handlePdfDownload}
          handleSendEmail={handleSendEmail}
          handleSaveQuote={handleSaveQuote}
          flipped={flipped}
          handleFlipToggle={handleFlipToggle}
          handleZoomIn={handleZoomIn}
          handleZoomOut={handleZoomOut}
          showOriginalToolbar={showOriginalToolbar}
          toggleOriginalToolbar={() => setShowOriginalToolbar(prev => !prev)}
        />
      )}

      <div className={`door-container-left ${showTopBar ? '' : 'shift-down'}`}>
        {/* Original Toolbar */}
        {(showOriginalToolbar || !enableFloatingToolbarMenu) && (
          <div className="toolbar-container">
            <div className="toolbar">
              <button
                className="toolbar-button"
                onClick={toggleViewDetails}
              >
                {isViewDetailsVisible ? "Hide Details" : "View Details"}
              </button>
              <button className="toolbar-button" onClick={togglePrices} disabled={!isViewDetailsVisible}>
                {displayPrices ? "Hide Prices" : "Show Prices"}
              </button>
              <button
                className={`toolbar-button ${isLoading ? "loading" : ""}`}
                onClick={handlePdfDownload}
                disabled={!isViewDetailsVisible || isLoading}
              >
                {isLoading ? <span className="spinner"></span> : "PDF"}
              </button>
              <button className="toolbar-button" onClick={() => handleSendEmail(true)} disabled={!isViewDetailsVisible}>
                Email
              </button>
              <button className="toolbar-button" onClick={() => handleSaveQuote(false)}>
                Save
              </button>
              <button className="toolbar-button" onClick={() => handleSaveQuote(true)}>
                Exit
              </button>

              <span
                className="arrow"
                onClick={() => setShowAltToolbar(prev => !prev)}
                title="Toggle more tools"
              >
                {showAltToolbar ? "▲" : "▼"}
              </span>
            </div>
            {showAltToolbar && (
              <div className="toolbar toolbar-secondary">
                <button className="toolbar-button" onClick={handleFlipToggle}>
                  {flipped ? 'Show Front' : 'Show Back'}
                </button>
                <button className="toolbar-button" onClick={handleZoomIn}>
                  Zoom In
                </button>
                <button className="toolbar-button" onClick={handleZoomOut}>
                  Zoom Out
                </button>
              </div>
            )}
          </div>
        )}

        <div>
          {showTopBar && (enableFloatingToolbarMenu || enableFloatingNavMenu) ? (
            // Caption lives inside the sticky header so the docked bars never
            // cover it on scroll; the whole block stays pinned at the top.
            <div style={{ position: 'sticky', top: 0, zIndex: 1000, backgroundColor: tc.panelBg, paddingBottom: '6px' }}>
              <div className="door">
                <p><b><i>{doorData.frameData.name || "Patio Door"}</i></b></p>
              </div>
              {enableFloatingToolbarMenu && (
                <TopBarToolbarMenu
                  isViewDetailsVisible={isViewDetailsVisible}
                  toggleViewDetails={toggleViewDetails}
                  displayPrices={displayPrices}
                  togglePrices={togglePrices}
                  isLoading={isLoading}
                  handlePdfDownload={handlePdfDownload}
                  handleSendEmail={handleSendEmail}
                  handleSaveQuote={handleSaveQuote}
                  flipped={flipped}
                  handleFlipToggle={handleFlipToggle}
                  handleZoomIn={handleZoomIn}
                  handleZoomOut={handleZoomOut}
                />
              )}
              {enableFloatingNavMenu && (
                <TopBarNavMenu
                  selectedSectionId={selectedSectionId}
                  showOnlySelected={showOnlySelectedSection}
                  disabledSections={disabledSections}
                  hiddenSections={hiddenSections}
                  onSelectSection={(id) => {
                    setShowOnlySelectedSection(true);
                    setSelectedSectionId(id);
                    setExpandedSections(prev => ({ ...prev, [id]: true }));
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                  onViewAll={() => {
                    setShowOnlySelectedSection(false);
                    window.scrollTo({ top: 0, behavior: 'smooth' });
                  }}
                />
              )}
            </div>
          ) : (
            <div>
              <div className="door">
                <p><b><i>{doorData.frameData.name || "Patio Door"}</i></b></p>
              </div>
            </div>
          )}

          {/* Render expandable sections */}
          {sections.map(section => {
            // Get controls for this section from controlMap (if any)
            const sectionControls = controlMap.filter(c => c.expandableSectionId === section.id);

            return isSectionVisible(section.id) && (
              <ExpandableSection
                key={section.id}
                title={section.title}
                panelLocation="2"
                controlMap={sectionControls}
                sectionId={section.id}
                state={expandedSections[section.id] || false}
                onExpandChange={handleSectionExpandChange}
                forceExpanded={getForceExpanded(section.id)}
              />
            );
          })}
        </div>
      </div>

      {/* Drawing Area */}
      <div
        className={
          `door-container ${showTopBar ? '' : 'shift-down'} image-container1 ` +
          (isViewDetailsVisible ? "hidden-left-panel" : "")
        }
      >
        <div className="fixed-container_new">
          <div className={`svg-flip-container ${flipped ? 'flipped' : ''}`}>
            {DrawComponent ? (
              <DrawComponent
                doorData={doorDataCopy2}
                toLeft={isViewDetailsVisible ? 40 - moreOffsetX : moreOffsetX}
                zoom={zoom}
                flip={flipped}
              />
            ) : (
              <div style={{
                width: 400,
                height: 500,
                border: '2px dashed #ccc',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f9f9f9'
              }}>
                <p style={{ color: '#999' }}>Drawing Component Placeholder</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* View Details Sliding Panel */}
      <ViewDetails
        isVisible={isViewDetailsVisible}
        data={viewDetailsData}
        doorData={doorDataCopy}
        updateCalcData={updateCalcData}
        zoom={zoom}
        drawing={
          <div className={`svg-flip-container ${flipped ? 'flipped' : ''}`} style={{ display: 'inline-block' }}>
            {DrawComponent ? (
              <DrawComponent
                doorData={doorDataCopy2}
                toLeft={isViewDetailsVisible ? 40 - moreOffsetX : 0}
                zoom={zoom}
              />
            ) : (
              <div style={{ width: 300, height: 400, border: '1px solid #ccc' }}>
                Drawing Placeholder
              </div>
            )}
          </div>
        }
      />

      {/* Email Dialog */}
      {showEmailDialog && (
        <EmailDialog
          isOpen={showEmailDialog}
          onClose={() => setShowEmailDialog(false)}
          isSending={isSending}
          setIsSending={setIsSending}
          customerEmail={customerEmail}
        />
      )}
    </div>
  );
};

export default PD;
